/**
 * Audit & Control container (قسم الرقابة والتدقيق, section 14; chat C34 — D-134, D-135, D-093, D-115): binds
 * `AuditControl` (packages/ui, old App.tsx:43634-44801) to the read routes `GET /audit/{movements,customers,purchases,
 * security,settings}` and to the journal's undo (`POST /journal/undo`).
 *
 *   figures  : every row is the server's (office time zone, D-135); this container formats decimal strings, the
 *              journal rows before / after a correction (amount in its own currency, R-A6) and the sentences.
 *   refresh  : live — every 15 s and after an undo (D-135; old: only when the tab was switched).
 *   undo     : reason → own PIN → API (D-093: `undo_movements`, a new record; the original stays).
 *   exports  : print / PDF / WhatsApp take the whole filtered set (`all=1`, D-135) on the generic A4 template, columns
 *              and titles as the old handlers (App.tsx:23219-23498); the printed entry number is the entry code (R-A6).
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PRINT_ROOT_CLASS, StatementA4, printDocument, type PrintColumn } from "@faylasuf/print";
import {
  AuditControl, ReasonDialog,
  type AuditCompare, type AuditCustomerRow, type AuditExport, type AuditFilters, type AuditLogRow, type AuditLogTone, type AuditMovementRow, type AuditPurchaseRow, type AuditTab,
} from "@faylasuf/ui";
import { SECURITY_AUDIT_KINDS, SETTINGS_AUDIT_KINDS } from "@faylasuf/contracts";
import type { AuditCustomerView, AuditCustomersList, AuditLogList, AuditLogView, AuditMovementView, AuditMovementsList, AuditPurchaseView, AuditPurchasesList, AuditQuery, JournalRowData } from "../api/types.js";

type AnyAuditList = AuditMovementsList | AuditCustomersList | AuditPurchasesList | AuditLogList;
import { useApp, useSession } from "../app/context.js";
import { errorMessage, isSessionLost } from "../app/errors.js";
import { usePinGate } from "../app/pin-gate.js";
import { usePrintOffice } from "../app/printOffice.js";
import { useToast } from "../app/toast.js";
import { fmt } from "./customers/format.js";
import { itemOf } from "./JournalPage.js";

const TAB_TOKENS: ReadonlyArray<readonly [AuditTab, string]> = [
  ["movements", "audit_logs"],
  ["customers", "audit_customers_log"],
  ["purchases", "audit_purchases_list"],
  ["security", "audit_security_log"],
  ["settings", "audit_settings_log"],
];
const EMPTY_FILTERS: AuditFilters = { employee: "", action: "", from: "", to: "", product: "", warehouse: "" };
const SYMBOL: Readonly<Record<string, string>> = { IQD: "د.ع", USD: "$" };

/** "1,000 $" / "152,500 د.ع" — every currency of a journal money map, in its own unit (R-A6: USD never 0). */
function amountStr(m: Readonly<Record<string, string>>): string {
  const parts = Object.entries(m).map(([c, v]) => `${fmt(v)} ${SYMBOL[c] ?? c}`);
  return parts.length > 0 ? parts.join(" + ") : "0 د.ع";
}
const mainAmount = (r: JournalRowData | null): string => (r ? amountStr(Object.keys(r.in).length > 0 ? r.in : r.out) : "-");

function toneOf(kind: string): AuditLogTone {
  if (/failure|locked|refused|revoked|rate_limited|deactivated|deleted|archived|redeem_failed/.test(kind)) return "rose";
  if (/created|added|completed|enrolled|redeemed|restored/.test(kind)) return "emerald";
  if (/change|updated|reset|role_changed/.test(kind)) return "amber";
  if (kind === "login_success" || kind === "login_password_ok" || kind === "logout") return "blue";
  return "indigo";
}
const short = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "-";
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return s.length > 120 ? `${s.slice(0, 117)}…` : s;
};

export function AuditPage() {
  const { t, locale, source } = useApp();
  const { session, hasPermission, sessionLost } = useSession();
  const printOffice = usePrintOffice();
  const toast = useToast();
  const qc = useQueryClient();
  const pin = usePinGate();
  const tz = session.office.timeZone;

  const tabs = useMemo(() => new Set(TAB_TOKENS.filter(([, tok]) => hasPermission(tok)).map(([tab]) => tab)), [hasPermission]);
  const [tab, setTab] = useState<AuditTab>(() => TAB_TOKENS.find(([, tok]) => hasPermission(tok))?.[0] ?? "movements");
  const [filtersByTab, setFiltersByTab] = useState<Record<AuditTab, AuditFilters>>({ movements: EMPTY_FILTERS, customers: EMPTY_FILTERS, purchases: EMPTY_FILTERS, security: EMPTY_FILTERS, settings: EMPTY_FILTERS });
  const filters = filtersByTab[tab];

  const queryOf = (tb: AuditTab, f: AuditFilters, all = false): AuditQuery => ({
    ...(f.from ? { from: f.from } : {}),
    ...(f.to ? { to: f.to } : {}),
    ...(f.employee ? { employee: f.employee } : {}),
    ...(f.action ? (tb === "security" || tb === "settings" ? { kind: f.action } : { action: f.action }) : {}),
    ...(tb === "purchases" && f.product ? { product: f.product } : {}),
    ...(tb === "purchases" && f.warehouse ? { warehouse: f.warehouse } : {}),
    ...(all ? { all: true } : {}),
  });
  const fetchTab = (tb: AuditTab, q: AuditQuery): Promise<AnyAuditList> =>
    tb === "movements" ? source.auditMovements(q) : tb === "customers" ? source.auditCustomers(q) : tb === "purchases" ? source.auditPurchases(q) : tb === "security" ? source.auditSecurity(q) : source.auditSettings(q);

  const q = useQuery({
    queryKey: ["audit", tab, filters],
    queryFn: () => fetchTab(tab, queryOf(tab, filters)),
    enabled: tabs.has(tab),
    refetchInterval: 15_000, // D-135 live refresh
    staleTime: 5_000,
    placeholderData: (prev: AnyAuditList | undefined) => prev,
  });
  if (q.error && isSessionLost(q.error)) sessionLost();
  const data = q.data;

  const fail = (e: unknown) => {
    if (isSessionLost(e)) sessionLost();
    else toast(errorMessage(t, e), "error");
  };

  // ---------------------------------------------------------------- labels (office time zone, D-135)
  const dateTimeOf = (iso: string) => new Date(iso).toLocaleString(locale === "ar" ? "ar-IQ" : "en-US", { timeZone: tz });
  const printDateOf = (iso: string) => {
    const p = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(iso));
    const g = (k: string) => p.find((x) => x.type === k)?.value ?? "";
    return `${g("year")}-${g("month")}-${g("day")} ${g("hour")}:${g("minute")}`;
  };

  const compareOf = (r: JournalRowData | null): AuditCompare | null =>
    r
      ? {
          id: `#${r.code}`,
          item: itemOf(t, r.type, r.params) || "-",
          c_in: Object.keys(r.in).length > 0 ? amountStr(r.in) : "-",
          c_out: Object.keys(r.out).length > 0 ? amountStr(r.out) : "-",
          currency: "USD" in r.in || "USD" in r.out ? "USD" : "IQD",
          wallet_name: r.boxName ?? "-",
          customer_name: r.partyName ?? "-",
          note: r.note ?? "-",
          operator_name: r.operator.username ?? t("ac.unknown_operator"),
        }
      : null;
  const changesOf = (m: AuditMovementView): string[] => {
    const b = m.before;
    const a = m.after;
    const out: string[] = [];
    if (m.action === "cancel") {
      if (b?.boxName) out.push(t("ac.chg_box_one", { v: b.boxName }));
      if (b?.partyName) out.push(t("ac.chg_customer_one", { v: b.partyName }));
      if (b?.note) out.push(t("ac.chg_note_one", { v: b.note }));
      return out;
    }
    if (m.action === "undo") return [t("ac.chg_restored")];
    const ib = b ? itemOf(t, b.type, b.params) : "-";
    const ia = a ? itemOf(t, a.type, a.params) : "-";
    if (ib !== ia) out.push(t("ac.chg_item", { from: ib, to: ia }));
    if (mainAmount(b) !== mainAmount(a)) out.push(t("ac.chg_amount", { from: mainAmount(b), to: mainAmount(a) }));
    if ((b?.boxName ?? null) !== (a?.boxName ?? null)) out.push(t("ac.chg_box", { from: b?.boxName ?? "-", to: a?.boxName ?? "-" }));
    if ((b?.note ?? null) !== (a?.note ?? null)) out.push(t("ac.chg_note", { from: b?.note || "-", to: a?.note || "-" }));
    return out;
  };
  const movementRow = (m: AuditMovementView): AuditMovementRow => {
    const ref = m.before ?? m.after;
    return {
      id: m.id,
      entryCode: ref?.code ?? String(m.number),
      action: m.action,
      affected: ref ? itemOf(t, ref.type, ref.params) : "-",
      changes: changesOf(m),
      oldAmount: mainAmount(m.before),
      newAmount: mainAmount(m.after ?? m.before),
      changedBy: m.actor.username ?? "-",
      changedAt: dateTimeOf(m.at),
      reason: m.reason,
      undone: m.undone,
      canUndo: m.undoOperationId !== null,
      before: compareOf(m.before),
      after: compareOf(m.after),
    };
  };

  const kindLabel = (k: string | null | undefined) => (k === "debt" ? t("ac.kind_debt") : k === "trust" ? t("ac.kind_trust") : (k ?? "-"));
  const fieldLabel = (f: string) => t(`ac.field_${f}`);
  const customerRow = (c: AuditCustomerView): AuditCustomerRow => {
    const name = c.customerName ?? "-";
    let details = "";
    let sub: string | null = null;
    switch (c.action) {
      case "edit_name": {
        const on = typeof c.before?.["name"] === "string" ? (c.before["name"] as string) : name;
        const nn = typeof c.after?.["name"] === "string" ? (c.after["name"] as string) : name;
        details = on !== nn ? t("ac.cd_edit_name", { from: on, to: nn }) : t("ac.cd_edit_fields");
        const fields = (c.changed ?? []).filter((x) => x !== "name");
        if (fields.length > 0) sub = t("ac.cd_changed", { fields: fields.map(fieldLabel).join("، ") });
        break;
      }
      case "delete_account":
        details = t("ac.cd_delete", { name, type: kindLabel(c.accountKind) });
        break;
      case "create_account":
        details = t("ac.cd_create", { name, type: kindLabel(c.accountKind), number: c.accountNumber ?? "-" });
        break;
      case "restore_account":
        details = t("ac.cd_restore", { name, type: kindLabel(c.accountKind) });
        break;
      case "account_transfer":
        details = t("ac.cd_transfer", { amount: fmt(c.amount ?? "0"), currency: SYMBOL[c.currency ?? "IQD"] ?? c.currency ?? "", from: c.from ?? "-", to: c.to ?? "-" });
        break;
      case "currency_conversion":
        details = t(c.direction === "USD_to_IQD" ? "ac.cd_conversion_to_iqd" : "ac.cd_conversion_to_usd", { amount: fmt(c.amount ?? "0"), rate: c.rate ?? "-" });
        sub = t("ac.cd_conversion_bal", { bu: fmt(c.beforeUsd ?? "0"), bi: fmt(c.beforeIqd ?? "0"), au: fmt(c.afterUsd ?? "0"), ai: fmt(c.afterIqd ?? "0") });
        break;
    }
    if (c.cancelled) details = `${details} (${t("ac.cancelled")})`;
    return { id: c.id, action: c.action, customerName: name, details, subDetails: sub, changedBy: c.actor.username ?? "-", changedAt: dateTimeOf(c.at) };
  };

  const purchaseRow = (p: AuditPurchaseView, i: number): AuditPurchaseRow => ({
    key: `${p.operationId}:${i}`,
    entry: p.code,
    product: p.product ?? t("ac.po_unknown_product"),
    newItem: p.newItem,
    note: p.note,
    warehouse: p.warehouse ?? t("ac.po_main_warehouse"),
    quantity: fmt(p.quantity),
    unitCost: fmt(p.unitCost),
    totalCost: fmt(p.totalCost),
    box: p.box,
    operator: p.operator.username ?? "-",
    at: dateTimeOf(p.at),
  });

  const logDetails = (l: AuditLogView): string => {
    const parts: string[] = [];
    const b = l.before;
    const a = l.after;
    if (b !== null || a !== null) {
      if (b && a && typeof b === "object" && typeof a === "object" && !Array.isArray(b) && !Array.isArray(a)) {
        const keys = new Set([...Object.keys(b as object), ...Object.keys(a as object)]);
        for (const k of keys) {
          const bv = (b as Record<string, unknown>)[k];
          const av = (a as Record<string, unknown>)[k];
          if (JSON.stringify(bv) !== JSON.stringify(av)) parts.push(`${k}: ${short(bv)} ← ${short(av)}`);
        }
      } else parts.push(`${short(b)} ← ${short(a)}`);
    } else if (l.changed && l.changed.length > 0) parts.push(t("ac.cd_changed", { fields: l.changed.join("، ") }));
    else for (const [k, v] of Object.entries(l.facts)) if (k !== "username" && k !== "action" && k !== "key") parts.push(`${k}: ${short(v)}`);
    if (l.reason) parts.push(t("ac.reason", { reason: l.reason }));
    return parts.length > 0 ? parts.join(" | ") : "-";
  };
  const logRow = (l: AuditLogView): AuditLogRow => ({
    id: l.id,
    kindLabel: t(`ac.kind.${l.kind}`),
    tone: toneOf(l.kind),
    user: l.actor.username ?? "-",
    entity: l.entity ?? "-",
    details: logDetails(l),
    device: [l.device.ip, l.device.userAgent].filter(Boolean).join(" · ") || "-",
    branch: l.branch ?? "-",
    at: dateTimeOf(l.at),
  });

  const rows: readonly unknown[] = data?.rows ?? [];
  const movementRows = tab === "movements" ? (rows as readonly AuditMovementView[]).map(movementRow) : [];
  const customerRows = tab === "customers" ? (rows as readonly AuditCustomerView[]).map(customerRow) : [];
  const purchaseRows = tab === "purchases" ? (rows as readonly AuditPurchaseView[]).map(purchaseRow) : [];
  const logRows = tab === "security" || tab === "settings" ? (rows as readonly AuditLogView[]).map(logRow) : [];
  const pv = tab === "purchases" && data && "totals" in data ? data : null;
  const kinds = tab === "security" ? SECURITY_AUDIT_KINDS : tab === "settings" ? SETTINGS_AUDIT_KINDS : [];

  // ---------------------------------------------------------------- undo (D-093)
  const [undoId, setUndoId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const undoTarget = undoId ? (rows as readonly AuditMovementView[]).find((r) => r.id === undoId) ?? null : null;
  const confirmUndo = async (reason: string) => {
    if (!undoTarget?.undoOperationId) return;
    setBusy(true);
    try {
      const opId = undoTarget.undoOperationId;
      const res = await pin.withPin((proof) => source.journalUndo({ operationId: opId, reason, idempotencyKey: crypto.randomUUID() }, proof.pin ?? ""), { message: t("ac.pin_undo"), allowSupervisor: false });
      if (!res) return;
      toast(res.status === "pending" ? t("jr.toast_pending") : t("ac.toast_undone"), "success");
      setUndoId(null);
      for (const k of [["audit"], ["journal"], ["accounts"], ["dashboard"], ["profits"]]) void qc.invalidateQueries({ queryKey: k });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- exports (the whole filtered set, D-135)
  const [report, setReport] = useState<{ title: string; columns: PrintColumn[]; rows: Array<Record<string, string>>; summary?: Array<{ label: string; value: string }>; count: number } | null>(null);
  const onExport = async (tb: AuditTab, _how: AuditExport) => {
    try {
      const all = await fetchTab(tb, queryOf(tb, filtersByTab[tb], true));
      if (tb === "movements") {
        const list = all.rows as readonly AuditMovementView[];
        setReport({
          title: t("ac.print_mov_title"),
          count: list.length,
          columns: [
            { header: t("ac.col_entry"), key: "id", numeric: true },
            { header: t("ac.pcol_action"), key: "action" },
            { header: t("ac.col_affected"), key: "affected" },
            { header: t("ac.col_details_amount"), key: "details" },
            { header: t("ac.pcol_by"), key: "changedBy" },
            { header: t("ac.pcol_changed_at"), key: "changedAt", numeric: true },
          ],
          rows: list.map((m) => {
            const r = movementRow(m);
            return { id: `#${r.entryCode}`, action: t(m.action === "cancel" ? "ac.paction_delete" : m.action === "edit" ? "ac.paction_edit" : "ac.paction_undo"), affected: r.affected, details: r.changes.join(" | ") || t("ac.other_details"), changedBy: r.changedBy, changedAt: printDateOf(m.at) };
          }),
        });
      } else if (tb === "customers") {
        const list = all.rows as readonly AuditCustomerView[];
        setReport({
          title: t("ac.print_cust_title"),
          count: list.length,
          columns: [
            { header: t("ac.col_action"), key: "action" },
            { header: t("ac.pcol_customer"), key: "affected" },
            { header: t("ac.pcol_cust_details"), key: "details" },
            { header: t("ac.pcol_by"), key: "changedBy" },
            { header: t("ac.pcol_changed_at"), key: "changedAt", numeric: true },
          ],
          rows: list.map((c) => {
            const r = customerRow(c);
            return { action: t(`ac.cust_${c.action}`), affected: r.customerName, details: r.subDetails ? `${r.details} [${r.subDetails}]` : r.details, changedBy: r.changedBy, changedAt: printDateOf(c.at) };
          }),
        });
      } else if (tb === "purchases") {
        const list = all.rows as readonly AuditPurchaseView[];
        const tot = "totals" in all ? all.totals : { entries: 0, quantity: "0", cost: "0", paid: "0" };
        setReport({
          title: t("ac.print_po_title"),
          count: list.length,
          columns: [
            { header: t("ac.col_entry"), key: "id", numeric: true },
            { header: t("ac.pcol_product"), key: "product" },
            { header: t("ac.pcol_warehouse"), key: "warehouse" },
            { header: t("ac.pcol_qty"), key: "quantity", numeric: true },
            { header: t("ac.pcol_unit"), key: "unitPrice", numeric: true },
            { header: t("ac.po_col_total"), key: "totalPrice", numeric: true },
            { header: t("ac.pcol_box"), key: "wallet" },
            { header: t("ac.po_employee"), key: "purchaser" },
            { header: t("ac.col_datetime"), key: "date", numeric: true },
          ],
          rows: list.map((p, i) => {
            const r = purchaseRow(p, i);
            return { id: `#${r.entry}`, product: r.product, warehouse: r.warehouse, quantity: r.quantity, unitPrice: `${r.unitCost} د.ع`, totalPrice: `${r.totalCost} د.ع`, wallet: r.box ?? t("ac.po_no_cash"), purchaser: r.operator, date: printDateOf(p.at) };
          }),
          summary: [
            { label: t("ac.psum_entries"), value: `${fmt(String(tot.entries))} ${t("ac.psum_entries_unit")}` },
            { label: t("ac.psum_qty"), value: `${fmt(tot.quantity)} ${t("ac.psum_items")}` },
            { label: t("ac.psum_cost"), value: `${fmt(tot.cost)} د.ع` },
          ],
        });
      } else {
        const list = all.rows as readonly AuditLogView[];
        setReport({
          title: t(tb === "security" ? "ac.tab_security" : "ac.tab_settings"),
          count: list.length,
          columns: [
            { header: t("ac.col_action"), key: "action" },
            { header: t("ac.col_user"), key: "user" },
            { header: t("ac.col_entity"), key: "entity" },
            { header: t("ac.col_log_details"), key: "details" },
            { header: t("ac.col_device"), key: "device" },
            { header: t("ac.pcol_changed_at"), key: "date", numeric: true },
          ],
          rows: list.map((l) => {
            const r = logRow(l);
            return { action: r.kindLabel, user: r.user, entity: r.entity, details: r.details, device: l.device.ip ?? "-", date: printDateOf(l.at) };
          }),
        });
      }
    } catch (e) {
      fail(e);
    }
  };
  useEffect(() => {
    if (!report) return;
    const done = () => setReport(null);
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [report]);

  const integrity = data?.integrity;
  return (
    <>
      <AuditControl
        tab={tab}
        onTabChange={setTab}
        tabs={tabs}
        integrityBroken={integrity && integrity.status === "broken" ? { problems: integrity.problems, checkedAt: integrity.checkedAt ? dateTimeOf(integrity.checkedAt) : "-" } : null}
        employees={data?.operators ?? []}
        filters={filters}
        onFilters={(f) => setFiltersByTab((s) => ({ ...s, [tab]: f }))}
        onExport={(tb, how) => void onExport(tb, how)}
        movementRows={movementRows}
        onUndo={setUndoId}
        customerRows={customerRows}
        purchaseRows={purchaseRows}
        purchaseTotals={{ entries: String(pv?.totals.entries ?? 0), quantity: fmt(pv?.totals.quantity ?? "0"), cost: fmt(pv?.totals.cost ?? "0"), paid: fmt(pv?.totals.paid ?? "0") }}
        products={pv?.products ?? []}
        warehouses={pv?.warehouses ?? []}
        logRows={logRows}
        logKinds={kinds.map((k) => ({ id: k, label: t(`ac.kind.${k}`) }))}
      />
      {undoTarget && (
        <ReasonDialog
          title={t("ac.undo_dialog_title", { code: `#${undoTarget.before?.code ?? undoTarget.number}` })}
          warning={<p>{t("ac.undo_dialog_warning")}</p>}
          confirmLabel={t("ac.undo_confirm")}
          busy={busy}
          onConfirm={(r) => void confirmUndo(r)}
          onClose={() => setUndoId(null)}
        />
      )}
      <div className="relative z-[130]">{pin.dialog}</div>
      {report &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            <StatementA4
              locale={locale}
              title={report.title}
              dateLabel={new Date().toLocaleDateString(locale === "ar" ? "ar-IQ" : "en-US", { timeZone: tz })}
              docNumber={String(report.count)}
              office={printOffice}
              {...(filtersByTab[tab].from ? { periodFrom: filtersByTab[tab].from } : {})}
              {...(filtersByTab[tab].to ? { periodTo: filtersByTab[tab].to } : {})}
              columns={report.columns}
              rows={report.rows}
              {...(report.summary ? { summary: report.summary } : {})}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
